import type { ReactNode } from "react";
import { Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Pill } from "@/components/ui/Pill";

export interface ScopeTab<T extends string> {
  value: T;
  label: ReactNode;
  /** How many it holds, once that's known; an unknown total is left out, not guessed. */
  count?: number;
  /** Other people's things, drawn like Search's Explore: a sparkle, accent when chosen. */
  explore?: boolean;
}

/**
 * Page scopes use underlines. Toggle filters can explicitly choose pills;
 * both keep the same selection semantics and horizontal overflow behavior.
 */
export function ScopeTabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  className,
  appearance = "underline",
}: {
  tabs: ScopeTab<T>[];
  value: T;
  onChange: (value: T) => void;
  /** What the choice is about, for assistive tech ("Collections", "Timeframe"). */
  label: string;
  className?: string;
  appearance?: "pill" | "underline";
}) {
  return (
    <div role="group" aria-label={label} className={cn("flex min-w-0 max-w-full overflow-x-auto scrollbar-hide", appearance === "underline" ? "gap-4 max-[420px]:gap-2.5" : "gap-2", className)}>
      {tabs.map(tab => {
        const chosen = tab.value === value;
        if (appearance === "underline") {
          return (
            <button
              key={tab.value}
              type="button"
              aria-pressed={chosen}
              onClick={() => onChange(tab.value)}
              className={cn(
                "inline-flex h-(--control-height) min-w-(--control-height) shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-0.5 text-ui-control font-ui focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2",
                chosen ? "border-foreground text-foreground" : "border-transparent text-muted-foreground",
              )}
            >
              {tab.explore && <Sparkles className="size-4" aria-hidden="true" />}
              {tab.label}
              {tab.count !== undefined && <span className="text-ui-micro text-muted-foreground">{tab.count.toLocaleString()}</span>}
            </button>
          );
        }
        return (
          <Pill
            key={tab.value}
            variant={chosen && !tab.explore ? "active" : "outlined"}
            size="md"
            pressed={chosen}
            onClick={() => onChange(tab.value)}
            className={cn("shrink-0", tab.explore && chosen && "border-brand-accent text-brand-accent")}
          >
            {tab.explore && <Sparkles className="h-4 w-4" aria-hidden="true" />}
            {tab.label}
            {tab.count !== undefined && <span className="text-ui-caption font-normal opacity-60">{tab.count.toLocaleString()}</span>}
          </Pill>
        );
      })}
    </div>
  );
}
